import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Table, Btn, Badge, Card, Modal, Input, Textarea } from "@/components/UI";
import { openPdfPreview } from "@/lib/pdfPreview";
import { ShieldAlert, Check, X, RefreshCw, Clock, Calendar, FileText } from "lucide-react";

export function QuarantinePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expiryModal, setExpiryModal] = useState(null);
  const [form, setForm] = useState({ new_expiry_date: "", notes: "" });
  const [historyModal, setHistoryModal] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [lots, mats] = await Promise.all([
        api.get("/lots", { status: "quarantine" }),
        api.get("/materials"),
      ]);
      setRows(lots || []);
      setMaterials(mats || []);
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mname = (id) => materials.find(m => m.id === id)?.name || "—";
  const mcode = (id) => materials.find(m => m.id === id)?.code || "—";
  const isExpiry = (r) => r.quarantine_reason === "EXPIRY_DATE_PASSED";
  const expiredRows = rows.filter(isExpiry);
  const qcRows = rows.filter(r => !isExpiry(r));

  const openExpiryQC = (r) => {
    setExpiryModal(r);
    // Suggest a date 3 months from today by default
    const d = new Date(); d.setMonth(d.getMonth() + 3);
    setForm({ new_expiry_date: d.toISOString().slice(0, 10), notes: "" });
  };
  const submitExpiryApprove = async () => {
    if (!form.new_expiry_date) { alert("Yeni SKT girilmelidir."); return; }
    try {
      await api.post(`/lots/${expiryModal.id}/qc-release-expiry`, {
        result: "approved",
        new_expiry_date: form.new_expiry_date,
        notes: form.notes,
      });
      setExpiryModal(null); setForm({ new_expiry_date: "", notes: "" });
      load();
    } catch (e) { alert(e.response?.data?.detail || "İşlem başarısız."); }
  };
  const submitExpiryReject = async () => {
    const reason = window.prompt("Red nedeni (audit'e kaydedilecek):", form.notes || "");
    if (reason === null) return;
    try {
      await api.post(`/lots/${expiryModal.id}/qc-release-expiry`, {
        result: "rejected",
        rejection_reason: reason,
      });
      setExpiryModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "İşlem başarısız."); }
  };

  // Legacy QC quarantine actions (kept from previous implementation)
  const reevaluate = async (r) => {
    if (!window.confirm(`"${r.lot_code}" lotu yeniden değerlendirilerek ana stoğa alınsın mı?`)) return;
    try { await api.post(`/lots/${r.id}/approve`, { notes: "Karantina sonrası yeniden değerlendirildi — uygun." }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Onay verilemedi"); }
  };
  const rejectPerm = async (r) => {
    const reason = window.prompt("Kalıcı ret sebebi (audit'e yazılır):", "");
    if (reason === null) return;
    try { await api.post(`/lots/${r.id}/block?reason=${encodeURIComponent(reason || "Kalıcı red")}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Ret verilemedi"); }
  };

  return (
    <div>
      <PageHeader
        title="Karantina Yönetimi"
        description={`SKT geçmiş ve QC'de "Uygun Değil" işaretlenmiş lotlar. QC yetkilisi lotu inceleyip yeni SKT ile serbest bırakabilir veya kalıcı olarak reddedebilir.`}
        actions={<Btn variant="secondary" onClick={load} disabled={busy} data-testid="qtn-refresh"><RefreshCw size={14}/> Yenile</Btn>}
      />

      {/* ------- SKT KARANTİNASI ------- */}
      <Card className="p-3 mb-3 bg-rose-50/70 border-rose-200 flex items-center gap-2">
        <Clock size={18} className="text-rose-700"/>
        <span className="text-sm text-rose-900"><b>{expiredRows.length}</b> lot SKT geçtiği için otomatik karantinada. QC değerlendirmesi ile yeni SKT girilerek yeniden aktif edilebilir.</span>
      </Card>
      <Table testid="qtn-expiry-table" rows={expiredRows} columns={[
        { label: "Kod", render: r => <span className="font-mono text-xs">{mcode(r.material_id)}</span> },
        { label: "Malzeme", render: r => mname(r.material_id) },
        { label: "Lot", render: r => <span className="font-mono text-xs">{r.lot_code}</span> },
        { label: "Miktar", render: r => <span className="font-mono">{r.qty_available} {r.unit || ""}</span> },
        { label: "Üretim", render: r => (r.mfg_date || "").slice(0, 10) || "—" },
        { label: "Eski SKT", render: r => <span className="text-rose-700 font-medium">{(r.original_expiry_date || r.expiry_date || "").slice(0, 10) || "—"}</span> },
        { label: "Karantina Tarihi", render: r => (r.quarantined_at || r.expiry_quarantine_at || "").slice(0, 10) || "—" },
        { label: "Kaynak", render: r => <Badge level="critical">SKT Karantinası</Badge> },
        { label: "QC", render: r => (r.expiry_history || []).length > 0 ? <button className="text-xs text-teal-700 underline" onClick={() => setHistoryModal(r)} data-testid={`qtn-hist-${r.id}`}><Calendar size={10} className="inline"/> Geçmiş ({(r.expiry_history || []).length})</button> : <span className="text-xs text-slate-400">—</span> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="approve" onClick={() => openExpiryQC(r)} data-testid={`qtn-expiry-qc-${r.id}`}><Check size={12}/> QC Değerlendir</Btn>
            {/* Iter 202 §24–25 — Karantina / Serbest PDF (READ-ONLY, does not change lot state). */}
            <Btn size="sm" variant="ghost" onClick={async () => {
              try { const blob = await api.getBlob(`/qc/quarantine/${r.id}/pdf`); openPdfPreview(blob, `Karantina_${r.lot_code}.pdf`); }
              catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
            }} data-testid={`qtn-expiry-pdf-${r.id}`} title="SKT Uyarı PDF"><FileText size={12}/></Btn>
          </div>
        )},
      ]} />
      {expiredRows.length === 0 && !busy && <div className="text-center py-4 text-slate-400 text-sm">✓ SKT karantinasında lot yok.</div>}

      {/* ------- QC KARANTİNASI (Legacy) ------- */}
      <Card className="p-3 mt-6 mb-3 bg-amber-50/60 border-amber-200 flex items-center gap-2">
        <ShieldAlert size={18} className="text-amber-700"/>
        <span className="text-sm text-amber-900"><b>{qcRows.length}</b> lot QC nedeniyle karantinada. Yeniden değerlendirme onaylı stoğa alır; kalıcı red bloklu duruma taşır.</span>
      </Card>
      <Table testid="qtn-table" rows={qcRows} columns={[
        { label: "Lot", render: r => <span className="font-mono text-xs">{r.lot_code}</span> },
        { label: "Malzeme", render: r => mname(r.material_id) },
        { label: "Tedarikçi", key: "supplier" },
        { label: "Miktar", render: r => <span className="font-mono">{r.qty_available} {r.unit || ""}</span> },
        { label: "SKT", render: r => (r.expiry_date || "").slice(0, 10) || "—" },
        { label: "Karantina Tarihi", render: r => (r.quarantined_at || "").slice(0, 16).replace("T", " ") || "—" },
        { label: "Alan Kişi", render: r => r.quarantined_by_name || "—" },
        { label: "Sebep / Not", render: r => <span className="text-xs text-slate-600 max-w-[240px] block truncate" title={r.qc_notes || ""}>{r.qc_notes || <span className="text-slate-400 italic">—</span>}</span> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            <Btn size="sm" variant="approve" onClick={() => reevaluate(r)} data-testid={`qtn-approve-${r.id}`}><Check size={12}/> Yeniden Değerlendir</Btn>
            <Btn size="sm" variant="danger" onClick={() => rejectPerm(r)} data-testid={`qtn-reject-${r.id}`}><X size={12}/> Kalıcı Red</Btn>
            {/* Iter 202 §24–25 — Karantina / Serbest PDF (READ-ONLY). */}
            <Btn size="sm" variant="ghost" onClick={async () => {
              try { const blob = await api.getBlob(`/qc/quarantine/${r.id}/pdf`); openPdfPreview(blob, `Karantina_${r.lot_code}.pdf`); }
              catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
            }} data-testid={`qtn-qc-pdf-${r.id}`} title="QC Bekleyen PDF"><FileText size={12}/></Btn>
          </div>
        )},
      ]} />

      {/* ------- SKT QC Modal ------- */}
      <Modal open={!!expiryModal} onClose={() => setExpiryModal(null)} title={`SKT Karantina QC — ${expiryModal?.lot_code || ""}`} size="lg">
        {expiryModal && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 border rounded">
              <div><span className="text-xs uppercase text-slate-500 block">Malzeme</span>{mname(expiryModal.material_id)} <span className="text-slate-400">({mcode(expiryModal.material_id)})</span></div>
              <div><span className="text-xs uppercase text-slate-500 block">Lot Numarası</span><span className="font-mono">{expiryModal.lot_code}</span></div>
              <div><span className="text-xs uppercase text-slate-500 block">Mevcut Miktar</span><span className="font-mono">{expiryModal.qty_available} {expiryModal.unit || ""}</span></div>
              <div><span className="text-xs uppercase text-slate-500 block">Üretim Tarihi</span>{(expiryModal.mfg_date || "").slice(0,10) || "—"}</div>
              <div><span className="text-xs uppercase text-slate-500 block">Eski SKT</span><span className="text-rose-700 font-medium">{(expiryModal.original_expiry_date || expiryModal.expiry_date || "").slice(0,10) || "—"}</span></div>
              <div><span className="text-xs uppercase text-slate-500 block">Karantinaya Alınma</span>{(expiryModal.quarantined_at || expiryModal.expiry_quarantine_at || "").slice(0,16).replace("T"," ") || "—"}</div>
            </div>
            <Input label="Yeni Son Kullanım Tarihi (bugünden sonra + eski SKT'den sonra)" type="date" value={form.new_expiry_date} onChange={e => setForm({ ...form, new_expiry_date: e.target.value })} data-testid="qtn-expiry-new-date"/>
            <Textarea label="QC Notu / Gerekçe (fiziksel görünüm, koku, pH, viskozite değerlendirmesi)" rows={4} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="qtn-expiry-notes"/>
            <div className="flex justify-end gap-2 mt-2">
              <Btn variant="secondary" onClick={() => setExpiryModal(null)}>Vazgeç</Btn>
              <Btn variant="danger" onClick={submitExpiryReject} data-testid="qtn-expiry-reject"><X size={12}/> Reddet</Btn>
              <Btn variant="approve" onClick={submitExpiryApprove} data-testid="qtn-expiry-approve"><Check size={12}/> Onayla & Yeni SKT ile Serbest Bırak</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ------- SKT Geçmiş Modal ------- */}
      <Modal open={!!historyModal} onClose={() => setHistoryModal(null)} title={`SKT Geçmişi — ${historyModal?.lot_code || ""}`} size="lg">
        {historyModal && (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">İlk SKT: <span className="font-mono text-slate-900">{(historyModal.original_expiry_date || "").slice(0,10)}</span></div>
            {(historyModal.expiry_history || []).map((h, i) => (
              <div key={h.id || i} className="border-l-2 border-teal-400 pl-3 py-1.5 text-sm">
                <div className="font-mono text-xs">{(h.previous_expiry_date || "—").slice(0,10)} → <span className="text-emerald-700 font-semibold">{(h.new_expiry_date || "").slice(0,10)}</span></div>
                <div className="text-xs text-slate-500">{(h.extended_at || "").slice(0,16).replace("T"," ")} — {h.extended_by_name}</div>
                {h.qc_notes && <div className="text-xs text-slate-600 italic mt-1">"{h.qc_notes}"</div>}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
